import React, { useState, useEffect } from 'react';
import { Button, message, Table, Modal, Form, Input, Select, Tag } from 'antd';
import { useLocation } from 'react-router-dom';
import type { TableColumnsType } from 'antd';
import CooperationWarehouseFormDrawer from './components/CooperationWarehouseFormDrawer';
import { useStationViewer } from '../address-review-pool/hooks/useStationViewer';
import FilterArea, { FilterOptions, getDefaultFilterOptions } from './components/FilterArea';
import { useQuery, useMutation } from '@apollo/client';
import { MERGE_COOPERATION_WAREHOUSE_ADDRESSES, MergeAddressInput, MergeAddressResponse } from './graphql/mutations/mergeAddressMutations';
import { GENERATE_COOPERATION_WAREHOUSE_BLACKLIST_DOWNLOAD_REPORT } from './graphql/mutations/cooperationWarehouseMutations';
import {
  GET_COOPERATION_WAREHOUSE_BY_ID,
  FindCooperationWarehouseByIdResponse,
  CooperationWarehouseFiltersTypeInput
} from './graphql/queries/getCooperationWarehouses';
import CooperationWarehouseBlacklistDownloadModal from './components/CooperationWarehouseBlacklistDownloadModal';
import { useWarehouseContext } from "../../../contexts/WarehouseContext";
import { usePickupSiteV2Gray } from '../../../hooks/usePickupSiteV2Gray';
import { useCooperationWarehouseQuery } from './hooks/useCooperationWarehouseQuery';
import { parseAddressIds, MAX_ADDRESS_IDS } from './utils/parseAddressIds';
import { formatTimestampToDateTime, formatTimeRangeToISO } from "../../../utils/dateTimeUtils";
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';



interface CooperationWarehousePageProps {
  /** 外层页签切回本页时 +1：列表数据可能已被另一个页签的操作改动，需重查而不是沿用挂载时的旧结果 */
  refreshSignal?: number;
}

const CooperationWarehousePage: React.FC<CooperationWarehousePageProps> = ({ refreshSignal }) => {
  const location = useLocation();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState<FilterOptions>(getDefaultFilterOptions());
  const [sortOption, setSortOption] = useState<string>('id_newest');
  const { currentWarehouse: warehouse } = useWarehouseContext();
  // 揽收站点 2.0 灰度：灰度 hub 显示建联状态(link_status 5 态)，非灰度 hub 保留原「状态」(记录状态 启用/禁用/待审核)。
  const { express2Enabled } = usePickupSiteV2Gray();
  const isV2 = express2Enabled((warehouse as any)?.id);

  // 合并地址相关状态
  const [mergeModalVisible, setMergeModalVisible] = useState(false);
  const [mergeForm] = Form.useForm();
  const [mergeLoading, setMergeLoading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState<{targetAddressId: string, similarIds: string[]}>({targetAddressId: '', similarIds: []});

  // 强制合并确认弹框状态
  const [forceMergeModalVisible, setForceMergeModalVisible] = useState(false);
  const [forceMergeData, setForceMergeData] = useState<{
    targetAddressId: string,
    similarIds: string[],
    targetAddress: {id: string, city: string, zipcode: string} | null,
    similarAddresses: {id: string, city: string, zipcode: string}[] | null
  }>({
    targetAddressId: '',
    similarIds: [],
    targetAddress: null,
    similarAddresses: null
  });

  const { t } = useTranslation();
  const statusOptions = [
    { label: t('enum.cooperationWarehouseStatus.active'), value: 1 },
    { label: t('enum.cooperationWarehouseStatus.suspended'), value: 0 },
    { label: t('enum.cooperationWarehouseStatus.pending'), value: 2 },
  ];
  // 揽收站点 2.0 · 需求点2：建联状态(link_status 5 态)筛选/展示，复用审核池 linkStatus 文案
  const linkStatusOptions = [1, 6, 2, 3, 4, 5].map((v) => ({
    label: t(`pickup.addressReviewPool.linkStatus.${v}`),
    value: v,
  }));
  // link_status → Tag 配色（与司机反馈 LS_COLOR 一致：1待上门橙/2揽收成功待建联蓝/3揽收失败待建联红/4已建联绿/5确认无法建联灰）
  const LINK_STATUS_COLOR: Record<number, string> = { 1: 'orange', 6: 'purple', 2: 'blue', 3: 'red', 4: 'green', 5: 'default' };

  // 排序选项配置
  const sortOptions = [
    { label: 'ID: Newest First', value: 'id_newest' },
    { label: 'ID: Oldest First', value: 'id_oldest' },
    { label: 'Address: A → Z', value: 'address_asc' },
    { label: 'Address: Z → A', value: 'address_desc' },
    { label: 'Zipcode: Low → High', value: 'zipcode_asc' },
    { label: 'Zipcode: High → Low', value: 'zipcode_desc' },
    { label: 'Updatetime: Newest First', value: 'updatetime_newest' },
    { label: 'Updatetime: Oldest First', value: 'updatetime_oldest' },
  ];
  // 处理URL参数，自动打开新增抽屉
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');

    if (action === 'create') {
      setSelectedWarehouse(null);
      setDrawerMode('create');
      setDrawerVisible(true);
      // 清除URL参数，避免刷新页面时重复打开
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.search]);

  // 解析「地址ID」多值输入：非法/超限时不下发 ids（避免发出坏查询），错误在筛选按钮显式提示
  const { id: _rawIdText, ...restFilters } = filters;
  const parsedAddressIds = parseAddressIds(filters.id);

  // 构建查询过滤器
  const queryFilters: CooperationWarehouseFiltersTypeInput = {
    ...restFilters,
    ids: parsedAddressIds.ids.length > 0 ? parsedAddressIds.ids : undefined,
    warehouseId: warehouse?.id!,
    state: filters.state && filters.state.trim() !== '' ? filters.state : undefined,
    city: filters.city && filters.city.trim() !== '' ? filters.city : undefined,
    zipcode: filters.zipcode && filters.zipcode.trim() !== '' ? filters.zipcode : undefined,
  };

  // 使用新的 hook 进行分页查询
  const { warehouseData: rawWarehouseData, loading, refetch, totalElements } = useCooperationWarehouseQuery(
    queryFilters,
    pagination.current,
    pagination.pageSize
  );

  // 页签切回时重查：antd Tabs 默认保留非活跃面板不卸载，不主动刷新就会一直显示切走前的数据
  useEffect(() => {
    if (refreshSignal) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // 筛选条件变化时把分页拉回第 1 页。
  //
  // FilterArea 是受控输入直接 setFilters，而 queryFilters 直接喂给 useQuery 的 variables，
  // 所以条件一改就立刻重查；但 pagination.current 原先只在 handleFilter（点「筛选」按钮）里重置。
  // 两者不同步导致：在第 N(>1) 页输入地址ID，查询带着旧页码发出，命中结果只有第 1 页有数据，
  // 第 N 页为空 → 运营以为"只在当前分页里搜、搜不到"，实际是全库搜了但翻到了不存在的页。
  //
  // 依赖用序列化值而非对象：queryFilters 每次渲染都是新对象，直接放依赖会每帧触发。
  // 用 current === 1 短路避免自触发循环。
  const queryFiltersKey = JSON.stringify(queryFilters);
  useEffect(() => {
    setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
  }, [queryFiltersKey]);

  // 灰度 hub：新建/编辑/查看复用审核池 2.0 站点表单（含 2.0 新增字段）；非灰度仍用老抽屉
  const { openViewStation, openEditStation, openCreateStation, viewerDrawer } = useStationViewer({
    onSaved: () => refetch(),
    // 2.2 需求点2：「AB地址映射」入口**只在站点管理这一处**开（PRD 原型画的就是这里）。
    // 司机反馈跟进详情与审核池详情共用同一个表单，默认关 —— 尤其审核池那边本就有一个
    // 「AB映射」动作（条目当 A），同屏两个 AB 入口而 A 的含义相反会把运营搞糊涂。
    showAbEntry: true,
  });

  // 排序逻辑
  const getSortedData = (data: any[]) => {
    if (!data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      // 地址排序键：忽略前缀数字/符号，从首个字母开始（不区分大小写）
      const getAddressSortKey = (addressLine1?: string, addressLine2?: string): string => {
        const combined = [addressLine1 || '', addressLine2 || ''].filter(Boolean).join(', ').trim();
        const match = combined.match(/[A-Za-z]/);
        if (!match) return combined.toLowerCase();
        const idx = match.index || 0;
        return combined.substring(idx).toLowerCase();
      };
      switch (sortOption) {
        case 'id_newest':
          return (b.id || 0) - (a.id || 0);
        case 'id_oldest':
          return (a.id || 0) - (b.id || 0);
        case 'address_asc':
          return getAddressSortKey(a.addressLine1, a.addressLine2)
            .localeCompare(getAddressSortKey(b.addressLine1, b.addressLine2));
        case 'address_desc':
          return getAddressSortKey(b.addressLine1, b.addressLine2)
            .localeCompare(getAddressSortKey(a.addressLine1, a.addressLine2));
        case 'zipcode_asc':
          return (a.zipcode || '').localeCompare(b.zipcode || '');
        case 'zipcode_desc':
          return (b.zipcode || '').localeCompare(a.zipcode || '');
        case 'updatetime_newest':
          return (b.updatedAt || 0) - (a.updatedAt || 0);
        case 'updatetime_oldest':
          return (a.updatedAt || 0) - (b.updatedAt || 0);
        default:
          return (b.id || 0) - (a.id || 0);
      }
    });
  };

  const warehouseData = getSortedData(rawWarehouseData);

  // Query single warehouse details
  const { refetch: refetchWarehouseDetail } = useQuery<FindCooperationWarehouseByIdResponse>(
      GET_COOPERATION_WAREHOUSE_BY_ID,
    {
      variables: { id: null },
      skip: true,
      fetchPolicy: 'network-only',
    }
  );

  // 合并地址 mutation
  const [mergeAddresses] = useMutation<MergeAddressResponse, { input: MergeAddressInput }>(
    MERGE_COOPERATION_WAREHOUSE_ADDRESSES
  );


  const handleSortChange = (value: string | undefined) => {
    // 当点击清除按钮时，重置为默认值
    setSortOption(value || 'id_newest');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pagination: any) => {
    setPagination({ current: pagination.current, pageSize: pagination.pageSize });
  };

  const showNewDrawer = () => {
    if (isV2) {
      openCreateStation(Number((warehouse as any)?.id));
      return;
    }
    setSelectedWarehouse(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showMergeModal = () => {
    setMergeModalVisible(true);
    mergeForm.resetFields();
  };

  // 黑名单下载 mutation
  const [blacklistFileName, setBlacklistFileName] = useState('');
  const [blacklistDownloadVisible, setBlacklistDownloadVisible] = useState(false);
  const [generateBlacklistDownload, { loading: blacklistLoading }] = useMutation<{ generateCooperationWarehouseBlacklistDownloadReport: string }>(
    GENERATE_COOPERATION_WAREHOUSE_BLACKLIST_DOWNLOAD_REPORT
  );

  const handleDownloadBlacklist = async () => {
    try {
      const { data } = await generateBlacklistDownload({
        variables: {
          filters: {
            ...queryFilters,
            createdAt: formatTimeRangeToISO(queryFilters.createdAt),
            updatedAt: formatTimeRangeToISO(queryFilters.updatedAt)
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      });
      const fileName = data?.generateCooperationWarehouseBlacklistDownloadReport;
      if (fileName) {
        setBlacklistFileName(fileName);
        setBlacklistDownloadVisible(true);
      }
    } catch (error: any) {
      message.error(error.message || t('pickup.cooperationWarehouse.messages.blacklistDownloadFailed'));
    }
  };

  const handleMergeCancel = () => {
    setMergeModalVisible(false);
    mergeForm.resetFields();
  };

  const showMergeConfirmModal = (targetAddressId: string, similarIds: string[]) => {
    setConfirmModalData({ targetAddressId, similarIds });
    setConfirmModalVisible(true);
  };

  const handleConfirmMerge = async (forceSubmit: boolean) => {
    await performMergeAddress(confirmModalData.targetAddressId, confirmModalData.similarIds, forceSubmit);
    setConfirmModalVisible(false);
    setConfirmModalData({targetAddressId: '', similarIds: []});
  };

  const handleCancelConfirm = () => {
    if (!mergeLoading) {
      setConfirmModalVisible(false);
      setConfirmModalData({targetAddressId: '', similarIds: []});
    }
  };

  // 显示强制合并确认弹框
  const showForceMergeModal = (targetAddressId: string, similarIds: string[]) => {
    // 从当前数据中查找目标地址信息
    const targetAddress = warehouseData.find(item => item.id?.toString() === targetAddressId);
    const targetAddressInfo = targetAddress ? {
      id: targetAddressId,
      city: targetAddress.city || '',
      zipcode: targetAddress.zipcode || ''
    } : null;

    // 从当前数据中查找相似地址信息
    const similarAddresses = similarIds.map(id => {
      const address = warehouseData.find(item => item.id?.toString() === id);
      return {
        id,
        city: address?.city || '',
        zipcode: address?.zipcode || ''
      };
    });

    setForceMergeData({
      targetAddressId,
      similarIds,
      targetAddress: targetAddressInfo,
      similarAddresses
    });
    setForceMergeModalVisible(true);
  };

  // 处理强制合并确认
  const handleForceMergeConfirm = async () => {
    await performMergeAddress(forceMergeData.targetAddressId, forceMergeData.similarIds, true);
    setForceMergeModalVisible(false);
    setForceMergeData({
      targetAddressId: '',
      similarIds: [],
      targetAddress: null,
      similarAddresses: null
    });
  };

  // 取消强制合并
  const handleForceMergeCancel = () => {
    if (!mergeLoading) {
      setForceMergeModalVisible(false);
      setForceMergeData({
        targetAddressId: '',
        similarIds: [],
        targetAddress: null,
        similarAddresses: null
      });
    }
  };

  const handleMergeSubmit = async () => {
    try {
      const values = await mergeForm.validateFields();
      const { targetAddressId, similarAddressIds } = values;

      // 解析相似地址ID列表并去重
      const similarIds = similarAddressIds
        .split(',')
        .map((id: string) => id.trim())
        .filter((id: string) => id)
        .filter((id: string, index: number, array: string[]) => array.indexOf(id) === index); // 去重

      // 显示确认弹窗
      showMergeConfirmModal(targetAddressId, similarIds);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const performMergeAddress = async (targetAddressId: string, similarIds: string[], forceSubmit: boolean) => {
    setMergeLoading(true);
    try {
      const response = await mergeAddresses({
        variables: {
          input: {
            targetAddressId: parseInt(targetAddressId),
            similarAddressIds: similarIds.map(id => parseInt(id)),
            forceSubmit: forceSubmit
          }
        }
      });

      const result = response.data?.mergeCooperationWarehouseAddresses;
      if (result?.success) {
        message.success(result.message || t('pickup.cooperationWarehouse.messages.addressMergeSuccessful') || '');
        setMergeModalVisible(false);
        setConfirmModalVisible(false);
        mergeForm.resetFields();
        setConfirmModalData({targetAddressId: '', similarIds: []});
        refetch(); // 刷新列表数据
      } else {
        if (result?.message === 'No similar addresses match the target address location (state, city, zipcode). Cannot perform merge operation.') {
            // 显示强制合并确认弹框
            showForceMergeModal(confirmModalData.targetAddressId, confirmModalData.similarIds);
        } else {
            let errorMessage = result?.message || t('pickup.cooperationWarehouse.messages.addressMergeFailed') || '';
            if (errorMessage === 'Target address ID cannot be in similar address IDs list') {
                errorMessage = t('pickup.cooperationWarehouse.messages.targetAddressCannotSimilarAddress');
            }
            // 揽收站点 2.0：目标站点非「已建联」时后端硬拒绝（forceSubmit 也不放行），
            // 原样透传的是内部英文串（含 link_status 数字），运营看不懂 → 转成带状态名的中文提示。
            // 依赖后端 performMergeAddresses 的异常文案格式，改动后端文案时需同步此处。
            const notLinkedMatch = errorMessage.match(/^Merge target must be linked.*linkStatus:\s*(\d+)/);
            if (notLinkedMatch) {
                errorMessage = t('pickup.cooperationWarehouse.messages.mergeTargetNotLinked', {
                    status: t(`pickup.addressReviewPool.linkStatus.${notLinkedMatch[1]}`),
                });
            }
            // 同上：被合并的源站点为「确认无法建联」时后端硬拒绝。合并会停用源站并把其地址并入目标站，
            // 等于把一个已确认联系不上的点变成目标站的有效揽收地址，司机会被派过去。
            const unreachableMatch = errorMessage.match(/^Cannot merge cooperation warehouses that are confirmed unreachable.*\[(.+)\]/);
            if (unreachableMatch) {
                errorMessage = t('pickup.cooperationWarehouse.messages.mergeSourceUnreachable', {
                    ids: unreachableMatch[1],
                });
            }
            // 揽收站点 2.0（hub 灰度）：相似判断改用地址库口径（analysis_hash + 大号），三道硬校验、
            // forceSubmit 不放行。后端抛英文，此处按前缀转中文；非灰度 hub 走老逻辑不会产生这三串。
            const notStandardizedMatch = errorMessage.match(/^Merge requires standardized addresses.*\[(.+)\]/);
            if (notStandardizedMatch) {
                errorMessage = t('pickup.cooperationWarehouse.messages.mergeNotStandardized', {
                    ids: notStandardizedMatch[1],
                });
            }
            if (/^Cannot merge: primary number differs/.test(errorMessage)) {
                // 大号不同是最常见也最危险的误操作，文案不带 id、直接说结论
                errorMessage = t('pickup.cooperationWarehouse.messages.mergePrimaryNumberDiffers');
            }
            const notSameAddressMatch = errorMessage.match(/^Cannot merge: not the same address.*\[(.+)\]/);
            if (notSameAddressMatch) {
                errorMessage = t('pickup.cooperationWarehouse.messages.mergeNotSameAddress', {
                    ids: notSameAddressMatch[1],
                });
            }
          message.error(errorMessage);
        }
      }
    } catch (error: any) {
      console.error('合并地址失败:', error);
      message.error(error.message || t('pickup.cooperationWarehouse.messages.addressMergeFailed') || '');
    } finally {
      setMergeLoading(false);
    }
  };

  const showDetailDrawer = async (record: any,type: any) => {
    if (isV2) {
      // 灰度 hub：查看/编辑走 2.0 站点表单（按 id 加载既有站点，含 2.0 字段）
      if (type === 'edit') openEditStation(Number(record.id), Number((warehouse as any)?.id));
      else openViewStation(Number(record.id));
      return;
    }
    // 1. 立即打开抽屉，显示加载动画
    setSelectedWarehouse(null);

    setDrawerMode(type);
    setDrawerVisible(true);
    
    try {
      // 2. 调用后端查询
      const result = await refetchWarehouseDetail({ id: Number(record.id) });
      const warehouseDetail = result.data?.getCooperationWarehouseById;
      
      if (warehouseDetail) {
        // 3. 设置数据，结束加载动画
        setSelectedWarehouse(warehouseDetail);
      } else {
        message.error(`${t('pickup.cooperationWarehouse.messages.warehouseDetailNotFound')} ${record.id}`);
        setDrawerVisible(false);
      }
    } catch (error) {
      console.error('Failed to load warehouse details:', error);
      message.error(`${t('pickup.cooperationWarehouse.messages.failedToLoadWarehouseDetails')}`);
      setDrawerVisible(false);
    }
  };

  const handleDrawerSubmit = async (values: any) => {
    // 检查是否是从展示模式切换到编辑模式
    if (values._switchToEdit) {
      setDrawerMode('edit');
      return; // 不关闭抽屉，继续编辑
    }

    if (drawerMode === 'create') {
      // Create logic - handled by CooperationWarehouseFormDrawer
    } else if (drawerMode === 'edit' && selectedWarehouse) {
      // Edit logic - handled by CooperationWarehouseFormDrawer
    }
    setDrawerVisible(false);
    refetch();
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('pickup.cooperationWarehouse.columns.id'),
      dataIndex: 'id',
      key: 'id',
      align: 'left' as const,
      ellipsis: false,
      render: (text: string, record: any) => (
        <Button type="link" onClick={() => showDetailDrawer(record,'view')} style={{ paddingLeft: 0 }}>
            <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
        </Button>
      ),
    },
    { 
      title: t('pickup.cooperationWarehouse.columns.name'), 
      dataIndex: 'name', 
      key: 'name', 
      align: 'left' as const,
      ellipsis: false,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    { 
      title: t('pickup.cooperationWarehouse.columns.hub'), 
      dataIndex: 'warehouseId', 
      key: 'warehouseId', 
      align: 'left' as const,
      ellipsis: false,
      render: (text, record) => <span style={{ whiteSpace: 'nowrap' }}>{record.warehouseName || '-'}</span>
    },
    { 
      title: t('pickup.cooperationWarehouse.columns.city'), 
      dataIndex: 'city', 
      key: 'city', 
      align: 'left' as const,
      ellipsis: false,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    { 
      title:  t('pickup.cooperationWarehouse.columns.state'), 
      dataIndex: 'state', 
      key: 'state', 
      align: 'left' as const,
      ellipsis: false,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    { 
      title:  t('pickup.cooperationWarehouse.columns.zipcode'), 
      dataIndex: 'zipcode', 
      key: 'zipcode', 
      align: 'left' as const,
      ellipsis: false,
      render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
    },
    {
      title: t('pickup.cooperationWarehouse.columns.address'),
      key: 'address',
      align: 'left' as const,
      ellipsis: false,
      render: (_: any, record: any) => {
        const addressLine1 = record.addressLine1 || '';
        const addressLine2 = record.addressLine2 || '';

        const parts = [addressLine1, addressLine2].filter(Boolean);
        const text = parts.length > 0 ? parts.join(',') : '-';
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    {
      title: t('pickup.cooperationWarehouse.columns.contactPerson'),
      key: 'contactName', 
      align: 'left' as const,
      ellipsis: false,
      render: (_: any, record: any) => {
        const firstContact = record.contactPersonList?.[0];
        const text = firstContact?.name || '-';
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    { 
      title: t('pickup.cooperationWarehouse.columns.phone'),
      key: 'phoneNumber', 
      align: 'left' as const,
      ellipsis: false,
      render: (_: any, record: any) => {
        const firstContact = record.contactPersonList?.[0];
        let text = '-';
        if (firstContact?.phone) {
          const countryCode = firstContact.countryCode || '';
          const extension = firstContact.extension ? `-${firstContact.extension}` : '';
          text = `+${countryCode} ${firstContact.phone}${extension}`;
        }
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      }
    },
    {
      // 揽收站点 2.0 · 需求点2：站点列表新增品牌列
      title: t('pickup.cooperationWarehouse.columns.brand'),
      dataIndex: 'brand',
      key: 'brand',
      align: 'left' as const,
      ellipsis: false,
      render: (v: string | undefined | null) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: t('pickup.cooperationWarehouse.filters.isNew'),
      dataIndex: 'isNew',
      key: 'isNew',
      align: 'left' as const,
      ellipsis: false,
      render: (value: number | boolean | undefined | null) => {
        const isYes = value === 1 || value === true;
        const isNo = value === 0 || value === false;
        const text = isYes ? t('enum.yesorNoStatus.yes') : isNo ? t('enum.yesorNoStatus.no') : '--';
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      },
    },
    {
      title: t('pickup.cooperationWarehouse.filters.continuousFailure'),
      dataIndex: 'continuousFailure',
      key: 'continuousFailure',
      align: 'left' as const,
      ellipsis: false,
      render: (value: number | boolean | undefined | null) => {
        const isYes = value === 1 || value === true;
        const isNo = value === 0 || value === false;
        const text = isYes ? t('enum.yesorNoStatus.yes') : isNo ? t('enum.yesorNoStatus.no') : '--';
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      },
    },
    {
      title: t('pickup.cooperationWarehouse.filters.standingPickup'),
      dataIndex: 'standingPickup',
      key: 'standingPickup',
      align: 'left' as const,
      ellipsis: false,
      render: (value: number | boolean | undefined | null) => {
        const isYes = value === 1 || value === true;
        const isNo = value === 0 || value === false;
        const text = isYes ? t('enum.yesorNoStatus.yes') : isNo ? t('enum.yesorNoStatus.no') : '--';
        return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
      },
    },
    (isV2
      ? {
          // 灰度 hub：站点「状态」= 建联状态 link_status 5 态（记录状态启用/禁用/待审核降为后端默认过滤，隐藏禁用）
          title: t('pickup.cooperationWarehouse.columns.linkStatus'),
          dataIndex: 'linkStatus',
          key: 'linkStatus',
          align: 'left' as const,
          ellipsis: false,
          render: (v: number | undefined | null) => {
            const opt = linkStatusOptions.find(o => o.value === v);
            if (v == null) {
              return <span className="text-gray-400">-</span>;
            }
            return <Tag color={LINK_STATUS_COLOR[v] || 'default'}>{opt?.label || '-'}</Tag>;
          },
        }
      : {
          // 非灰度 hub：保留原「状态」= 记录状态（启用1/禁用0/待审核2）
          title: t('pickup.cooperationWarehouse.columns.status'),
          dataIndex: 'status',
          key: 'status',
          align: 'left' as const,
          ellipsis: false,
          render: (status: number) => {
            const statusOption = statusOptions.find(option => option.value === status);
            const text = statusOption?.label || 'Unknown';
            let color = '';
            switch (status) {
              case 1:
                color = 'text-green-600';
                break;
              case 0:
                color = 'text-red-500';
                break;
              case 2:
                color = 'text-orange-500';
                break;
              default:
                color = 'text-gray-400';
            }
            return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
          },
        }),
      {
          title: t('pickup.appointment.appointmentPageDetails.fields.remark'),
          key: 'remark',
          align: 'left' as const,
          dataIndex: 'remark',
          ellipsis: false,
          render: (text: string) => {
              if (!text) return <span style={{ whiteSpace: 'nowrap' }}>-</span>;

              const displayText = text.length > 15 ? `${text.substring(0, 15)}...` : text;
              return (
                  <span title={text} style={{ whiteSpace: 'nowrap' }}>
                      {displayText}
                  </span>
              );
          }
      },
    { 
      title: t('pickup.cooperationWarehouse.columns.createdTime'), 
      dataIndex: 'createdAt', 
      key: 'createdAt', 
      align: 'left' as const,
      ellipsis: false,
      render: (text: string, record: any) => {
        const formatted = formatTimestampToDateTime(record.createdAt);
        return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
      }
    },
    { 
      title: t('pickup.cooperationWarehouse.columns.updatedTime'), 
      dataIndex: 'updatedAt', 
      key: 'updatedAt', 
      align: 'left' as const,
      ellipsis: false,
      render: (value) => {
        const formatted = formatTimestampToDateTime(value);
        return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
      }
    },
      {
          title: t('components.button.action'),
          key: 'action',
          width: 120,
          align: 'left' as const,
          fixed: 'right',
          render: (_, record: any) => {
              return (
                  <div className="flex gap-2">
                      <Button
                          type="link"
                          onClick={() => showDetailDrawer(record, 'edit')}
                          style={{ paddingLeft: 0 }}
                      >
                          <span style={{ whiteSpace: 'nowrap' }}>{t('components.button.edit')}</span>
                      </Button>
                  </div>
              );
          }
      }

  ];

  // 处理筛选（当用户点击筛选按钮时，重置到第一页）
  const handleFilter = () => {
    const parsed = parseAddressIds(filters.id);
    if (parsed.error === 'tooMany') {
      message.error(t('pickup.cooperationWarehouse.messages.tooManyAddressIds', { max: MAX_ADDRESS_IDS }));
      return;
    }
    if (parsed.error === 'invalid') {
      message.error(t('pickup.cooperationWarehouse.messages.invalidAddressId'));
      return;
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 处理重置筛选
  const handleResetFilters = () => {
    setFilters(getDefaultFilterOptions());
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
      <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
        {/* Filter Area */}
        <FilterArea
          filters={filters}
          setFilters={setFilters}
          isV2={isV2}
          statusOptions={statusOptions}
          linkStatusOptions={linkStatusOptions}
          sortOption={sortOption}
          onSortChange={handleSortChange}
          sortOptions={sortOptions}
        />
        {/* 批量操作区域 */}
        <div className="flex justify-end items-center mb-3 pl-0">
          {/* 右侧 - 所有按钮 */}
          <div className="flex items-center gap-2">
            <Button
                type="primary"
                onClick={showNewDrawer}
                className="w-auto min-w-24"
            >
                <span className="font-bold">{t('components.button.new')}</span>
            </Button>
            <Button
              type="primary"
              onClick={handleFilter}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('components.button.filter')}</span>
            </Button>
            <Button
              onClick={handleResetFilters}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('components.button.reset')}</span>
            </Button>
            <Button
                onClick={showMergeModal}
                className="w-auto min-w-24"
            >
                <span className="font-bold">{t('pickup.cooperationWarehouse.buttons.mergeAddress')}</span>
            </Button>
            <Button
                type="primary"
                onClick={handleDownloadBlacklist}
                loading={blacklistLoading}
                className="w-auto min-w-24"
            >
                <span className="font-bold">{t('pickup.cooperationWarehouse.buttons.downloadBlacklist')}</span>
            </Button>
          </div>
        </div>

        {/* Table */}
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={warehouseData}
          pagination={false}
          scroll={{x: 'max-content'}}
          size="middle"
          tableLayout="auto"
         className="mb-4"/>

        {/* 分页 */}
        <TablePaginationBar
          total={totalElements}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
        />

        {/* Create/Edit/View Drawer（非灰度老抽屉） */}
        <CooperationWarehouseFormDrawer
          visible={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          onSubmit={handleDrawerSubmit}
          initialValues={selectedWarehouse || undefined}
          mode={drawerMode}
          statusOptions={statusOptions}
        />

        {/* 灰度 hub 的 2.0 站点表单（新建/编辑/查看） */}
        {viewerDrawer}

        {/* 合并地址弹窗 */}
        <Modal
          title={t('pickup.cooperationWarehouse.pageDetails.titles.mergeAddress')}
          open={mergeModalVisible}
          onOk={handleMergeSubmit}
          onCancel={handleMergeCancel}
          confirmLoading={mergeLoading}
          okText={t('components.button.confirm')}
          cancelText={t('components.button.cancel')}
          width={500}
        >
          <Form
            form={mergeForm}
            layout="vertical"
            style={{ marginTop: 16 }}
          >
            <Form.Item
              label={t('pickup.cooperationWarehouse.pageDetails.targetAddressId')}
              name="targetAddressId"
              rules={[
                { required: true, message: t('pickup.cooperationWarehouse.messages.pleaseEnterTargetAddressId') || '' },
                { pattern: /^\d+$/, message: t('pickup.cooperationWarehouse.messages.pleaseEnterAValidNumericId') || '' }
              ]}
            >
              <Input placeholder={t('pickup.cooperationWarehouse.placeholders.enterTargetAddressIdOnlyOneAllowed') || ''} />
            </Form.Item>

            <Form.Item
              label={t('pickup.cooperationWarehouse.pageDetails.similarAddressIds')}
              name="similarAddressIds"
              rules={[
                { required: true, message: t('pickup.cooperationWarehouse.messages.pleaseEnterSimilarAddressIds') || '' },
                {
                  pattern: /^(\d+\s*,\s*)*\d+$/,
                  message: t('pickup.cooperationWarehouse.messages.pleaseEnterValidIdListSeparatedByCommas') || ''
                }
              ]}
            >
              <Input.TextArea
                rows={3}
                placeholder={t('pickup.cooperationWarehouse.placeholders.enterSimilarAddressIdsMultipleIdsSeparatedByCommas') || ''}
              />
            </Form.Item>

            <div style={{ color: '#666', fontSize: '12px', marginTop: '-8px' }}>
              <p>• {t('pickup.cooperationWarehouse.pageDetails.targetAddress')}: {t('pickup.cooperationWarehouse.pageDetails.targetAddressDescription')}</p>
              <p>• {t('pickup.cooperationWarehouse.pageDetails.similarAddress')}: {t('pickup.cooperationWarehouse.pageDetails.similarAddressDescription')}</p>
            </div>
          </Form>
        </Modal>

        {/* 确认合并弹窗 */}
        <Modal
          title={t('pickup.cooperationWarehouse.pageDetails.titles.confirmMergeAddress')}
          open={confirmModalVisible}
          onOk={() => handleConfirmMerge(false)}
          onCancel={handleCancelConfirm}
          okText={mergeLoading ? t('components.button.processing') || '' : t('components.button.confirm') || ''}
          cancelText={t('components.button.cancel') || ''}
          okType="primary"
          okButtonProps={{
            disabled: mergeLoading,
            style: {
              width: '100px',
            }
          }}
          cancelButtonProps={{
            style: {
              width: '100px'
            }
          }}
          closable={false}
          maskClosable={false}
          keyboard={!mergeLoading}
          width={550}
        >
          <div>
            <p>{t('pickup.cooperationWarehouse.messages.confirmMergeAddressMessage', {similarIds: confirmModalData.similarIds.join(', '), targetAddressId: confirmModalData.targetAddressId})}</p>
          </div>
        </Modal>

        {/* 强制合并确认弹框 */}
        <Modal
          title={
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Address Merge Alert
            </div>
          }
          open={forceMergeModalVisible}
          onOk={handleForceMergeConfirm}
          onCancel={handleForceMergeCancel}
          okText="Confirm"
          cancelText="Cancle"
          okType="default"
          okButtonProps={{
            disabled: mergeLoading,
            style: {
              width: '80px',
              height: '32px',
              backgroundColor: '#1890ff',
              color: '#fff',
              border: 'none'
            }
          }}
          cancelButtonProps={{
            style: {
              width: '80px',
              height: '32px',
              border: '1px solid #d9d9d9',
              backgroundColor: '#fff',
              color: '#000'
            }
          }}
          closable={false}
          maskClosable={false}
          keyboard={!mergeLoading}
          width="25%"
          style={{ top: '20%' }}
        >
          <div className="space-y-4">
            {/* 警告消息 */}
            <div className="text-left">
              <br/>
              <p className="text-base text-black">
                The <span className="text-red-500 font-semibold">Zip Code / City</span> of the main address and the linked address do <span className="text-red-500 font-semibold">not match</span>:
              </p>
            </div>

            {/* 目标地址信息 - 整行展示 */}
            <div className="bg-gray-50 p-3 rounded border">
              <p className="font-bold text-black">
                Target Address (ID: {forceMergeData.targetAddress?.id || forceMergeData.targetAddressId}): City = {forceMergeData.targetAddress?.city || 'N/A'}, Zip Code = {forceMergeData.targetAddress?.zipcode || 'N/A'}
              </p>
            </div>

            {/* 空行分隔 */}
            <div></div>

            {/* 相似地址信息 - 所有地址放在一个框里 */}
            <div className="bg-gray-50 p-3 rounded border">
              {forceMergeData.similarAddresses?.map((address, index) => (
                <div key={address.id} className="font-bold text-black mb-1">
                  Similar Address (ID: {address.id}): City = {address.city || 'N/A'}, Zip Code = {address.zipcode || 'N/A'}
                </div>
              )) || (
                <div className="text-center text-gray-500">
                  No similar addresses found
                </div>
              )}
            </div>

            {/* 确认问题 */}
            <div className="text-left pt-2">
              <p className="text-base text-black font-medium">
                Do you want to proceed with the merge?
              </p>
            </div>
          </div>
        </Modal>

        <CooperationWarehouseBlacklistDownloadModal
          visible={blacklistDownloadVisible}
          onClose={() => setBlacklistDownloadVisible(false)}
          fileName={blacklistFileName}
        />
      </div>
  );
};

export default CooperationWarehousePage;
